<template>
  <div class="py-3">
    <div class="p-toolbar">
      <div class="p-toolbar-item p-search">
        <p-search-input
          v-model="search"
          :items="searchSuggestions"
        ></p-search-input>
      </div>
      <div v-if="hasAccess()" class="p-toolbar-item p-right">
        <v-btn
          v-if="hasAccess('write', 'settings')"
          :to="{ name: 'settingsContractTemplates' }"
          color="primary"
          class="p-btn-text mr-4"
          text
          large
        >
          {{ $t('contract.templates') }}
        </v-btn>
        <v-btn
          large
          rounded
          class="p-btn-rounded mr-4"
          @click.stop="openCreateDialog()"
        >
          {{ $t('contract.createContract') }}
        </v-btn>
        <v-btn
          large
          rounded
          class="p-btn-rounded mr-1"
          @click.stop="openUploadContractDialog()"
        >
          {{ $t('contract.uploadContract') }}
        </v-btn>
      </div>
    </div>

    <v-row>
        <v-col>
          <v-data-table
            :headers="tableHeaders"
            :items="contractsComputed"
            :search="search"
            :page.sync="page"
            :items-per-page="itemsPerPage"
            hide-default-footer
            no-data-text=""
            class="p-no-borders"
            @page-count="pageCount = $event"
          >
            <template v-slot:[`item.changed`]="{ item }">
              <v-tooltip v-if="item.changed" bottom>
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on">
                    <span v-if="item.changed" class="item-changed"></span>
                  </v-btn>
                </template>
                {{ $t('contract.table.changedTooltip') }}
              </v-tooltip>
            </template>
            <template v-slot:[`item.created`]="{ item }">
              {{ formatDate(item.created) }}
            </template>
            <template v-slot:[`item.signers`]="{ item }">
              <p-contact-item
                v-for="signer in item.signers"
                :key="signer.id"
                :contact="shortContactById(signer.id) || {}"
                :tooltip="signer.status === contractSignerStatus.signed && signer.statusTime ? `${$t('contract.signedAt')} ${formatDateTime(signer.statusTime)}`:''"
                :avatar-class="`${signer.status === contractSignerStatus.signed ? 'mod-signed' : ''} mr-2`"
                avatar-only
              >
                <span v-if="signer.status === contractSignerStatus.signed" class="signer-status">
                  <p-contract-status :hasTitle="false"
                                     :value="contractStatus.signed">
                  </p-contract-status>
                </span>
              </p-contact-item>
            </template>
            <template v-slot:[`item.provider`]="{ item }">
              {{ (signingProviderById(item.providerId) || {}).name }}
            </template>
            <template v-slot:[`item.status`] ="{ item }">
              <p-contract-status :value="item.status"></p-contract-status>
            </template>
            <template v-slot:[`item.pdf`]="{ item }">
              <p-btn-icon
                icon="mdi-file-outline"
                :tooltip="$t('download')"
                @click="download(item)"
              ></p-btn-icon>
            </template>
            <template v-if="hasAccess()" v-slot:[`item.actions`]="{ item }">
              <div class="p-table-row-actions p-visible-on-row-hover">
                <p-btn-icon
                  v-if="item.status === contractStatus.new"
                  icon="mdi-arrow-right-circle-outline"
                  :tooltip="$t('contract.send')"
                  @click="setContractStatus_(item, contractStatus.pendingSign)"
                ></p-btn-icon>
                <p-btn-icon
                  v-if="item.status === contractStatus.pendingSign || item.status === contractStatus.sentForSign"
                  icon="mdi-close-circle-outline"
                  :tooltip="$t('contract.cancel')"
                  @click="setContractStatus_(item, contractStatus.cancelled)"
                ></p-btn-icon>
                <p-btn-icon
                  v-if="item.status === contractStatus.cancelled || item.status === contractStatus.rejected"
                  icon="mdi-refresh"
                  :tooltip="$t('contract.resend')"
                  @click="setContractStatus_(item, contractStatus.pendingSign)"
                ></p-btn-icon>

                <span>
                <p-btn-icon
                  v-if="item.status === contractStatus.new || item.status === contractStatus.cancelled"
                  type="edit"
                  @click="openEditDialog(item)"
                ></p-btn-icon>
                <p-btn-icon
                  type="delete"
                  @click="deleteContract_(item)"
                ></p-btn-icon>
              </span>
              </div>
            </template>
            <template v-slot:[`no-data`]>
              <p-no-data
                v-if="contractsByProjectLoaded(currentProjectId)"
                :text="$t('contract.noContracts')"
                :btn-text="$t('contract.createContract')"
                :hide-btn="!hasAccess()"
                img="contract.png"
                class="p-no-data-mt"
                @btn-click="openCreateDialog()"
              ></p-no-data>
            </template>
          </v-data-table>

          <v-pagination
            v-if="contractsByProject(currentProjectId).length > 0"
            v-model="page"
            :length="pageCount"
            :total-visible="15"
            class="mt-5"
          ></v-pagination>
        </v-col>
      </v-row>

    <contract-create-dialog
      :dialog.sync="createDialog"
    ></contract-create-dialog>

    <contract-upload-dialog
      :dialog.sync="uploadDialog"
    ></contract-upload-dialog>

    <contract-edit-dialog
      :dialog.sync="editDialog"
      :contract="editedContract"
      @download="download(editedContract)"
    ></contract-edit-dialog>
  </div>
</template>


<script>
import { mapGetters, mapActions } from 'vuex'

import { contractStatus, contractSignerStatus } from '@/constants'

import moment from 'moment'

import fileHelper from '@/helpers/file.helper'

import PContactItem from '@/components/PContactItem'
import PContractStatus from '@/components/PContractStatus'
import PSearchInput from '@/components/PSearchInput'
import PNoData from '@/components/PNoData'
import PBtnIcon from '@/components/PBtnIcon'

import ContractCreateDialog from './ContractCreateDialog'
import ContractUploadDialog from './ContractUploadDialog'
import ContractEditDialog from './ContractEditDialog'

import snackbarMixin from '@/mixins/snackbar.mixin'
import pageMixin from '@/mixins/page.mixin'
import objectMixin from '@/mixins/object.mixin'
import errorMixin from '@/mixins/error.mixin'
import accessMixin from '@/mixins/access.mixin'

import eventBus from '@/eventBus'


export default {
  components: {
    PContactItem,
    PContractStatus,
    PSearchInput,
    PNoData,
    PBtnIcon,
    ContractCreateDialog,
    ContractUploadDialog,
    ContractEditDialog
  },


  mixins: [
    snackbarMixin,
    pageMixin,
    objectMixin,
    errorMixin,
    accessMixin
  ],


  data () {
    return {
      search: '',
      page: 1,
      pageCount: 0,
      itemsPerPage: 20,
      createDialog: false,
      uploadDialog: false,
      editDialog: false,
      editedContract: null,
      contractStatus,
      contractSignerStatus
    }
  },


  computed: {
    ...mapGetters('contract', ['contractsByProject', 'contractsByProjectLoaded']),
    ...mapGetters('signingProvider', ['signingProviderById']),
    ...mapGetters('contact', ['shortContactById']),
    ...mapGetters('project', ['currentProjectId']),
    ...mapGetters('changes', ['isItemChanged']),

    contractsComputed () {
      return this.contractsByProject(this.currentProjectId).map(contract => {
        return {
          ...contract,
          changed: this.isItemChanged(contract.id)
        }
      })
    },

    tableHeaders () {
      return [
        { text: this.$t('contract.table.title'), value: 'title' },
        { text: '', value: 'changed' },
        { text: this.$t('contract.table.created'), value: 'created' },
        { text: this.$t('contract.table.signers'), value: 'signers' },
        { text: this.$t('contract.table.provider'), value: 'provider' },
        { text: this.$t('contract.table.status'), value: 'status' },
        { text: this.$t('contract.table.download'), value: 'pdf', sortable: false, filterable: false },
        { text: '', value: 'actions', align: 'end', sortable: false, filterable: false }
      ]
    },

    searchSuggestions () {
      const items = this.contractsByProject(this.currentProjectId).map(contract => { return contract.title })
      return items
    }
  },


  watch: {
    async currentProjectId () {
      if (!this.currentProjectId) return false
      this.startPageLoading()
      this.loadMembersByProject(this.currentProjectId)
      this.loadEventsByProject(this.currentProjectId)
      await this.loadContractsByProject(this.currentProjectId)
      this.stopPageLoading()
    }
  },


  methods: {
    ...mapActions('contract', ['loadContractsByProject', 'getContractPdf', 'deleteContract', 'setContractStatus']),
    ...mapActions('document', ['getDocument']),
    ...mapActions('signingProvider', ['loadSigningProviders']),
    ...mapActions('contact', ['loadShortContacts']),
    ...mapActions('member', ['loadMembersByProject']),
    ...mapActions('event', ['loadEventsByProject']),
    ...mapActions('contractTemplate', ['loadContractTemplates']),
    ...mapActions('user', ['loadUsersShort']),

    async searchContracts (val) {
      this.search = val
    },

    initEventBus () {
      eventBus.$on('goToEntity', async (entityTitle) => {
        this.searchContracts(entityTitle)
      })

      eventBus.$on('loadEntities', async () => {
        await this.loadContractsByProject(this.currentProjectId)
      })
    },


    formatDate (date) {
      return date ? moment(date).format('MMM D, yyyy') : ''
    },

    formatDateTime (date) {
      return date ? moment(date).format('HH:mm, MMM D, yyyy') : ''
    },

    openCreateDialog () {
      this.createDialog = true
    },

    openUploadContractDialog () {
      this.uploadDialog = true
    },

    openEditDialog (contract) {
      this.editedContract = contract
      this.editDialog = true
    },

    download (contract) {
      if (contract.status === contractStatus.signed) {
        this.downloadSignedContract(contract)
      } else {
        this.downloadPdf(contract)
      }
    },

    async downloadPdf (contract) {
      try {
        const pdf = await this.getContractPdf(contract)
        const blob = new Blob([pdf], { type: 'application/pdf' })
        fileHelper.downloadFile(blob, contract.title || 'contract')
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    async downloadSignedContract (contract) {
      try {
        const response = await this.getDocument(contract.fileId)
        const blob = new Blob([response.data], { type: response.headers['content-type'] })
        fileHelper.downloadFile(blob, contract.title || 'signedContract')
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    async setContractStatus_ (contract, status) {
      try {
        this.showSnackbarProcessing()
        await this.setContractStatus({ id: contract.id, projectId: contract.projectId, status })
        this.showSnackbarSuccess()
      } catch (error) {
        if (this.is405NotAllowedError(error)) {
          this.hideSnackbar()
          const errorData = this.getErrorData(error)
          switch (errorData?.type) {
            case 'ProviderEmpty':
              this.$alert(this.$t('contract.setStatusError.providerEmpty'), { type: 'error' })
              break
            case 'SignersEmpty':
              this.$alert(this.$t('contract.setStatusError.signersEmpty'), { type: 'error' })
              break
            case 'SignersWithoutDetails': {
              const signersWithoutDetails = errorData.data.map(id => {
                return (this.shortContactById(id) || {}).displayNameWithCompany
              }).join(', ')
              this.$alert(this.$t('contract.setStatusError.signersWithoutDetails', { signers: signersWithoutDetails }), { type: 'error' })
              break
            }
            default:
              this.showSnackbarError()
          }
        } else {
          this.showSnackbarError()
        }
        this.$log.error(error)
      }
    },

    async deleteContract_ (contract) {
      try {
        const confirm = await this.$confirm(this.$t('contract.deleteContractConfirm', contract), { trueText: this.$t('delete') })
        if (confirm === true) {
          this.showSnackbarProcessing()
          await this.deleteContract(contract)
          this.showSnackbarSuccess(this.$t('contract.contractDeleted'))
        }
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    }
  },

  async created () {
    this.initEventBus()
    if (!this.currentProjectId) return false
    try {
      this.startPageLoading()
      this.loadSigningProviders()
      this.loadShortContacts()
      if (this.hasAccess('read', 'settings')) {
        this.loadContractTemplates({ skip: 0 })
      }
      this.loadMembersByProject(this.currentProjectId)
      this.loadEventsByProject(this.currentProjectId)
      this.loadUsersShort()
      await this.loadContractsByProject(this.currentProjectId)
      if (this.$router.currentRoute.params?.goto) {
        await this.searchContracts(this.$router.currentRoute.params?.goto)
      }
    } catch (error) {
      this.showSnackbarError()
      this.$log.error(error)
    } finally {
      this.stopPageLoading()
    }
  }
}
</script>
